/**
 * learningConfig.js — Cycle 7 · Learning Science Layer runtime config
 * ────────────────────────────────────────────────────────────────────
 * The masteryEngine reads its thresholds from this module INSTEAD of
 * hardcoded constants so an admin (see /admin/learning-config) can
 * retune them without a rebuild. Values live in a single global row
 * `learning_config` on the backend (GET /api/learning-config); we
 * cache in memory + localStorage and fall back to sensible defaults
 * so the learner path NEVER breaks on a corrupt/missing config.
 */
const DEFAULTS = {
  masteryThreshold: 85,
  masteryWindow: 5,
  correctGainFirstTry: 15,
  correctGainNormal: 10,
  incorrectPenalty: 8,
  reviewIntervals: [1, 3, 7, 21],
  interleaveMax: 2,
  pathLength: 4,
  // Feb 2026 · Emergent Command · Learning Strategy Upgrades.
  // (1) 10-Minute Daily Quest framing — habit + activation.
  //     `dailyQuestMinutes` is a *frame*, never a lockout timer.
  //     `dailyQuestActivityCount` shapes the path rail to N stops.
  dailyQuestMinutes: 10,
  dailyQuestActivityCount: 3,
  // (2) Transfer-check (unfamiliar-problem) cadence.
  //     After N *mastered* activities in a strand, resurface the
  //     same skill in a DIFFERENT existing activity type.
  transferCheckEvery: 5,
  transferCheckFormatVary: true,
  // (3) "Why" reveal — display-only. On every activity completion,
  //     surface a short concept-visible sentence in Compass's voice.
  //     Enabled by default; can be turned off per-strand later.
  whyReveal: true,
  // Feb 2026 · Emergent Command · Learning Science Spine.
  // High-dependency skills earn a gain-multiplier (weight > 1) so
  // they mature faster AND spiral back more persistently in reviews.
  // The map is intentionally sparse — unlisted skills default to 1.
  dependencyWeights: {
    // Reading foundations
    "phonics": 1.6,
    "phonemic-awareness": 1.6,
    "letter-sounds": 1.5,
    "blending": 1.5,
    "sight-words": 1.3,
    // Arithmetic foundations
    "number-sense": 1.6,
    "place-value": 1.6,
    "counting": 1.4,
    "addition-facts": 1.3,
    "subtraction-facts": 1.3,
  },
};

const LS_KEY = "we_learning_config_v1";
let _memory = null;

function _fromLS() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return _validate(parsed);
  } catch { return null; }
}

function _validate(cfg) {
  if (!cfg || typeof cfg !== "object") return null;
  const out = { ...DEFAULTS, ...cfg };
  // Clamp thresholds.
  out.masteryThreshold = Math.max(0, Math.min(100, Number(out.masteryThreshold) || DEFAULTS.masteryThreshold));
  out.masteryWindow    = Math.max(1, Math.min(50,  Math.round(Number(out.masteryWindow)) || DEFAULTS.masteryWindow));
  out.correctGainFirstTry = Math.max(0, Number(out.correctGainFirstTry) || DEFAULTS.correctGainFirstTry);
  out.correctGainNormal   = Math.max(0, Number(out.correctGainNormal)   || DEFAULTS.correctGainNormal);
  out.incorrectPenalty    = Math.max(0, Number(out.incorrectPenalty)    || DEFAULTS.incorrectPenalty);
  out.interleaveMax = Math.max(1, Math.round(Number(out.interleaveMax)) || DEFAULTS.interleaveMax);
  out.pathLength    = Math.max(1, Math.round(Number(out.pathLength))    || DEFAULTS.pathLength);
  const iv = Array.isArray(out.reviewIntervals) ? out.reviewIntervals : DEFAULTS.reviewIntervals;
  out.reviewIntervals = iv.map((n) => Math.max(1, Math.round(Number(n)))).filter((n) => Number.isFinite(n));
  if (!out.reviewIntervals.length) out.reviewIntervals = [...DEFAULTS.reviewIntervals];
  // Dependency weight map: keep only positive numeric values,
  // clamp between 0.5 and 3.0 so a mis-config can never brick
  // progression.
  const dw = (out.dependencyWeights && typeof out.dependencyWeights === "object")
    ? out.dependencyWeights : DEFAULTS.dependencyWeights;
  out.dependencyWeights = Object.fromEntries(
    Object.entries(dw)
      .filter(([, v]) => Number.isFinite(Number(v)))
      .map(([k, v]) => [k, Math.max(0.5, Math.min(3, Number(v)))])
  );
  return out;
}

/** Look up the dependency weight for a skill. Unlisted skills → 1. */
export function getDependencyWeight(skill) {
  const cfg = getConfig();
  const map = cfg.dependencyWeights || {};
  if (map[skill] != null) return map[skill];
  // Also match by prefix ("phonics-cvc" → picks up "phonics" if
  // present) so we never miss a foundational skill just because the
  // slug got specialised.
  const key = Object.keys(map).find((k) => skill && skill.startsWith(k));
  return key ? map[key] : 1;
}

export function getConfig() {
  if (_memory) return _memory;
  _memory = _fromLS() || { ...DEFAULTS };
  return _memory;
}

export function getReviewIntervalDays() {
  return getConfig().reviewIntervals;
}

export function setConfigLocal(next) {
  _memory = _validate(next) || { ...DEFAULTS };
  try { localStorage.setItem(LS_KEY, JSON.stringify(_memory)); } catch { /* ignore */ }
  return _memory;
}

export function resetConfig() {
  _memory = { ...DEFAULTS };
  try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
  return _memory;
}

/** Fetch the server-side config (best-effort). Fall back to defaults
 *  on network/JSON error so we never break the learner path. */
export async function refreshFromServer(baseUrl = "") {
  try {
    const r = await fetch(`${baseUrl}/api/learning-config`);
    if (!r.ok) throw new Error(String(r.status));
    const j = await r.json();
    const clean = _validate(j.config || j);
    if (clean) setConfigLocal(clean);
    return clean || getConfig();
  } catch { return getConfig(); }
}

export const DEFAULT_CONFIG = { ...DEFAULTS };
